import React, { FC, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';


import Box from '@mui/material/Box';
import Link from '@mui/material/Link';
import Button from '@mui/material/Button';
import Stack from '@mui/material/Stack';
// react icons
import { BsGlobe } from 'react-icons/bs';
import { BiUserCircle } from 'react-icons/bi';
import { AiOutlineMenu } from 'react-icons/ai';
import { FaRegUserCircle } from 'react-icons/fa';
import { flexCenter } from 'themes/commonStyles';
import IconButton from '@mui/material/IconButton';
import AccountCircleIcon from '@mui/icons-material/AccountCircle';

import ManageAccountsIcon from '@mui/icons-material/ManageAccounts';
import LogoutIcon from '@mui/icons-material/Logout';
import MenuItem from '@mui/material/MenuItem';
import ListItemIcon from '@mui/material/ListItemIcon';
import Menu from '@mui/material/Menu';
import Divider from '@mui/material/Divider';
import { currentUserSessionAtom, currentUserSessionSetAuthentication, colorsAtom, useSideBarMenuAtom } from 'library/store';
import { useRecoilState, useSetRecoilState } from 'recoil';
import Typography from '@mui/material/Typography';
import { pink } from '@mui/material/colors';
import { Avatar, Chip, ListItemText, MenuList, Paper, Popover, useTheme } from '@mui/material';
import { ChevronRight, Logout } from '@mui/icons-material';



const ProfileSettings = () => {

  const navigate = useNavigate();
  const { t, i18n } = useTranslation();

  const theme = useTheme();

  const [Colors, setColors] = useRecoilState(colorsAtom);
  const [useSideBarMenu, setUseSideBarMenu] = useRecoilState(useSideBarMenuAtom);


  const [currentUserSession, setCurrentUserSession] = useRecoilState(currentUserSessionAtom);
  const { userDescription, userName } = currentUserSession;
  const disconnectUser = useSetRecoilState(currentUserSessionSetAuthentication);

  const [anchorEl, setAnchorEl] = useState(null);
  const open = Boolean(anchorEl);


  const handleClick = (event: any) => {
    setAnchorEl(event.currentTarget);
  }

  const handleClose = () => {
    setAnchorEl(null);
  }

  const menuItemClickDisconnect = (event: any) => {
    disconnectUser(currentUserSession);
    navigate('/');
  }

  const menuItemClickPasswordChange = (event: any) => {
    navigate('/passwordChange');
  }

  if (useSideBarMenu) {
    return <Stack sx={{
      direction: "row"
    }}>

      <Button onClick={handleClick}>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: .5,
          }}
        >
          <Avatar src="" alt="" sizes='small' 
          >
            {userDescription}
          </Avatar>
        </Box>
      </Button>

      <Popover
        id="profile"
        open={Boolean(anchorEl)}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        PaperProps={{
          elevation: 0,
          sx: {
            overflow: 'visible',
            filter: 'drop-shadow(0px 4px 20px rgba(0,0,0,0.08))',
            mt: 0.5,
            borderRadius: 1.5,
            minWidth: 260,
            maxWidth: 280,
            position: 'relative',
          },
        }}
      >
        <Box sx={{ p: 0 }}>
          <Box
            sx={{
              px: 2,
              py: 2,
              bgcolor: 'background.paper',
              backgroundImage: 'linear-gradient(to bottom right, #ebf8ff, #e0e7ff)',
              borderBottom: '1px solid',
              borderColor: 'divider',
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Avatar
                src="/static/images/avatar/1.jpg"
                alt="unisoft"
                sx={{
                  width: 32,
                  height: 32,
                  border: '1.5px solid white',
                  boxShadow: '0 1px 5px rgba(0,0,0,0.12)',
                }}
              />
              <Box sx={{ flex: 1 }}>
                <Typography
                  variant="body1"
                  sx={{ fontWeight: 600, color: 'text.primary', mb: 0.15 }}
                >
                  {userName}
                </Typography>

                <Chip
                  label={userDescription}
                  size="small"
                  sx={{
                    backgroundColor: 'rgba(59, 130, 246, 0.1)',
                    color: 'rgb(59, 130, 246)',
                    fontWeight: 500,
                    fontSize: '0.6rem',
                    height: 18,
                    px: 0.5,
                  }}
                />
              </Box>
            </Box>
          </Box>

          <Paper elevation={0} sx={{ p: 0, m: 0 }}>
            <MenuList dense>
              {[
                {
                  label: t('Change Password'),
                  icon: (
                    <ManageAccountsIcon sx={{ color: 'rgb(249, 115, 22)', fontSize: 16 }} />
                  ),
                  bgColor: 'rgba(249, 115, 22, 0.1)',
                  onClick: menuItemClickPasswordChange,
                  chip: null,
                  isLast: true,
                },
              ].map(({ label, icon, bgColor, onClick, chip, isLast }) => (
                <MenuItem
                  key={label}
                  onClick={onClick}
                  sx={{
                    borderRadius: 1,
                    mb: isLast ? 0.75 : 0.25,
                    px: 1.5,
                    py: 1,
                    transition: 'all 0.2s ease',
                    '&:hover': {
                      backgroundColor: 'grey.100',
                    },
                  }}
                >
                  <ListItemIcon sx={{ minWidth: 0, mr: 2 }}>
                    <Box
                      sx={{
                        p: 1,
                        borderRadius: 1,
                        backgroundColor: bgColor,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}
                    >
                      {icon}
                    </Box>
                  </ListItemIcon>
                  <ListItemText
                    primary={label}
                    primaryTypographyProps={{
                      variant: 'body2',
                      fontWeight: 500,
                      color: 'text.primary',
                      fontSize: '0.8rem',
                    }}
                    secondaryTypographyProps={{
                      variant: 'caption',
                      color: 'text.secondary',
                      fontSize: '0.6rem',
                    }}
                  />
                  {chip || (
                    <ChevronRight
                      sx={{ color: 'rgb(156, 163, 175)', fontSize: 14 }}
                    />
                  )}
                </MenuItem>
              ))}

              <Divider sx={{ my: 1 }} />

              <MenuItem
                onClick={menuItemClickDisconnect}
                sx={{
                  borderRadius: 1,
                  px: 1.5,
                  py: 1,
                  transition: 'all 0.2s ease',
                  border: '1px solid transparent',
                  mb: 0,
                  '&:hover': {
                    backgroundColor: 'rgba(254, 226, 226, 0.5)',
                    borderColor: 'rgba(254, 202, 202, 0.5)',
                  },
                }}
              >
                <ListItemIcon sx={{ minWidth: 0, mr: 2 }}>
                  <Box
                    sx={{
                      p: 1,
                      borderRadius: 1,
                      backgroundColor: 'rgba(254, 202, 202, 0.5)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    <Logout sx={{ color: 'rgb(239, 68, 68)', fontSize: 16 }} />
                  </Box>
                </ListItemIcon>
                <ListItemText
                  primary={t('Logout')}
                  primaryTypographyProps={{
                    variant: 'body2',
                    fontWeight: 500,
                    color: 'error.main',
                    fontSize: '0.8rem',
                  }}
                  secondaryTypographyProps={{
                    variant: 'caption',
                    color: 'error.dark',
                    fontSize: '0.6rem',
                  }}
                />
              </MenuItem>
            </MenuList>
          </Paper>
        </Box>
      </Popover>



      {/* <Menu anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        onClick={handleClose}
        PaperProps={{
          elevation: 0,
          sx: {
            overflow: 'visible',
            filter: 'drop-shadow(0px 2px 8px rgba(0,0,0,0.32))',
            mt: 1.5,
            '& .MuiAvatar-root': {
              width: 32,
              height: 32,
              ml: -0.5,
              mr: 1,
            },
            '&:before': {
              content: '""',
              display: 'block',
              position: 'absolute',
              top: 0,
              right: 14,
              width: 10,
              height: 10,
              bgcolor: 'background.paper',
              transform: 'translateY(-50%) rotate(45deg)',
              zIndex: 0,
            },
          },
        }}
        transformOrigin={{ horizontal: 'right', vertical: 'top' }}
        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
      >

        <MenuItem onClick={menuItemClickPasswordChange}>
          <ListItemIcon color="primary">
            <ManageAccountsIcon fontSize="small" color="primary" />
          </ListItemIcon>
          {t('Change Password')}
        </MenuItem>
        <Divider />

        <MenuItem onClick={menuItemClickDisconnect}>
          <ListItemIcon color="primary">
            <LogoutIcon fontSize="small" color="primary" />
          </ListItemIcon>
          {t('Logout')}
        </MenuItem>
      </Menu> */}
    </Stack>
  }
  return (
    <Box sx={{ ...flexCenter, mr: 3 }} >
      <Typography
        sx={{
          color: theme.palette.getContrastText(Colors.primary), // (theme) => theme.palette.text.primary,
          fontWeight: 'bold', paddingLeft: '16px',
          display: { xs: 'none', md: 'block' }
        }} >
        {userDescription}
      </Typography>

      <Stack>
        <Button onClick={handleClick}>
          <BiUserCircle size={24} color={theme.palette.getContrastText(Colors.primary)} />
        </Button>


        <Menu anchorEl={anchorEl}
          open={open}
          onClose={handleClose}
          onClick={handleClose}
          PaperProps={{
            elevation: 0,
            sx: {
              overflow: 'visible',
              filter: 'drop-shadow(0px 2px 8px rgba(0,0,0,0.32))',
              mt: 1.5,
              '& .MuiAvatar-root': {
                width: 32,
                height: 32,
                ml: -0.5,
                mr: 1,
              },
              '&:before': {
                content: '""',
                display: 'block',
                position: 'absolute',
                top: 0,
                right: 14,
                width: 10,
                height: 10,
                bgcolor: 'background.paper',
                transform: 'translateY(-50%) rotate(45deg)',
                zIndex: 0,
              },
            },
          }}
          transformOrigin={{ horizontal: 'right', vertical: 'top' }}
          anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
        >

          <MenuItem onClick={menuItemClickPasswordChange}>
            <ListItemIcon color="primary">
              <ManageAccountsIcon fontSize="small" color="primary" />
            </ListItemIcon>
            {t('Change Password')}
          </MenuItem>
          <Divider />

          <MenuItem onClick={menuItemClickDisconnect}>
            <ListItemIcon color="primary">
              <LogoutIcon fontSize="small" color="primary" />
            </ListItemIcon>
            {t('Logout')}
          </MenuItem>
        </Menu>
      </Stack>
    </Box>
  );
};

export default ProfileSettings;